import React, { forwardRef, useEffect, useRef, useState } from "react";
import PaginationComponent from "../PaginationComponent";
import Arrow from "./img/back.svg?react";
import "./style.scss";
import { useWindowSize } from "../../Helpers";
import { useLocation, useNavigate } from "react-router";
import { removeUrlParam } from "../../UrlParamsHelper";
import { useQuery } from "@tanstack/react-query";
import {
  DETAILS_CACHE_TIME,
  DETAILS_REFETCH_INVERVAL,
  DETAILS_STALE_TIME,
  getUserDetails,
} from "../../api/consumer";
import CloseButton from "@components/CloseButton";
import Crown from "./img/crown.svg?react";
import DOMPurify from "dompurify";

const OnboardingPopup = forwardRef(
  ({ content, currentStep, stepsLength, onClick, onExit }, ref) => (
    <div className="onboarding-popup-wrapper" ref={ref}>
      <div className="onboarding-popup">
        <CloseButton onClick={onExit} />
        <div className="tooltip" />
        <div className="content">
          <h4 dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.title)}} />
          <div className="onboarding-content">{content.desc}</div>
        </div>
        <div className="progress">
          <PaginationComponent current={currentStep + 1} total={stepsLength} />
          <button aria-label="next" onClick={onClick}>
            <div className="inner">
              {content.customButtonCopy && (
                <span
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.customButtonCopy)}}
                />
              )}
              <Arrow />
            </div>
          </button>
        </div>
      </div>
    </div>
  )
);

export default function Onboarding({ show }) {
  const [step, setStep] = useState(0);
  const element = useRef(null);
  const tooltip = useRef(null);
  const highlight = useRef(null);
  const windowSize = useWindowSize();
  const navigate = useNavigate();
  const location = useLocation();

  const { data } = useQuery({
    queryKey: ["user-details"],
    queryFn: getUserDetails,
    refetchInterval: DETAILS_REFETCH_INVERVAL,
    staleTime: DETAILS_STALE_TIME,
    cacheTime: DETAILS_CACHE_TIME,
  });

  const stepsMap = [
    {
      elementSelector: ".navigation >.navigation-top-bar",
      customHighlightStyling: (elementBoundingRect) => ({
        border: "none",
        height: `0`,
        width: `0`,
      }),
      switchContent: true,
      tooltipContent: {
        title: "",
        desc: "",
      },
      tooltipPositionOffsetDesktop: {
        top: 15,
        right: 25,
      },
      tooltipPositionOffsetMobile: {
        left: 20,
        bottom: 25,
      },

      higlightHidden: true,
    },
    {
      elementSelector: ".item[href='/activities']",
      tooltipContent: {
        title: "<strong>Punkte </strong>sammeln",
        desc: (
          <p>
            Quiz und andere Aufgaben{" "}
            <strong>ermöglichen es dir, Punkte zu erhalten</strong> und deinem
            Traumgutschein näher zu kommen.
          </p>
        ),
      },
      tooltipPositionOffsetDesktop: {
        top: -18,
        right: 75,
      },
      tooltipPositionOffsetMobile: {
        left: 0,
        bottom: 45,
      },
    },
    {
      elementSelector: ".item[href='/invite']",
      tooltipContent: {
        title: "<strong>Freunde </strong>einladen",
        desc: (
          <p>
            Verdiene Punkte und Gutscheine für{" "}
            <strong>erfolgreiche Empfehlungen.</strong>
          </p>
        ),
      },
      tooltipPositionOffsetDesktop: {
        top: -18,
        right: 75,
      },
      tooltipPositionOffsetMobile: {
        left: 0,
        bottom: 45,
      },
    },
    {
      menuCanHide: true,
      disableElementAutoFit: true,
      elementSelector:
        ".story-tiles-swiper .swiper-wrapper>.swiper-slide>div>div",
      customHighlightStyling: (elementBoundingRect) => ({
        top: `${elementBoundingRect.top - 12}px`,
        borderRadius: "50%",
        boxShadowWidth: "60px",
        height: `${elementBoundingRect.height + 24}px`,
      }),
      tooltipContent: {
        title: "<strong>Bleib auf dem Laufenden</strong>",
        desc: (
          <p>
            Mit<strong> Inspiration Stories </strong> verpasst du keine
            Neuigkeiten, Neuheiten, Angebote oder Events
            <strong> und sammelst außerdem Punkte!</strong>
          </p>
        ),
      },
      tooltipPositionOffsetDesktop: {
        top: -44,
        right: 64,
      },
      tooltipPositionOffsetMobile: {
        left: 16,
        top: 80,
      },
      tooltipMobileSettings: (elementBoundingRect) => ({
        inset: "initial",
        borderBottomColor: "#2A2C2C",
        bottom: "-24px",
        transform: "rotate(-90deg)",
        left: `${elementBoundingRect.width / 2 - 20 - 16}px`,
      }),
    },
    {
      elementSelector: ".item[href='/coupons']",
      tooltipContent: {
        title: "<strong> Punkte </strong><br/> eintauschen",
        desc: (
          <p>
            <strong>Tausche deine Punkte</strong> gegen Gutscheine ein und spare
            Geld.
          </p>
        ),
      },
      tooltipPositionOffsetDesktop: {
        top: -18,
        right: 75,
      },
      tooltipPositionOffsetMobile: {
        left: 0,
        bottom: 45,
      },
      action: () => {
        if (windowSize.width < 1020) {
          hideMenu();
        }

        let tokenUUID;

        try {
          tokenUUID = JSON.parse(getCookie("ICToken")).uuid;
        } catch (e) {
          try {
            tokenUUID = JSON.parse(localStorage.getItem("token")).uuid;
          } catch (e) {
            tokenUUID = null;
          }
        }

        let tutorialSeenTokens = JSON.parse(
          localStorage.getItem("tutorialSeenUUIDs") ?? "[]"
        );

        tutorialSeenTokens.push(tokenUUID);
        localStorage.setItem(
          "tutorialSeenUUIDs",
          JSON.stringify(tutorialSeenTokens)
        );

        closeOnboarding();
      },
    },
  ];

  const getLevelDescription = (level) => {
    switch (level) {
      case 1:
        return "Einsteiger";
      case 2:
        return "Profi";
      case 3:
        return "Experte";
      default:
        return "unknown";
    }
  };

  const level = getLevelDescription(data?.consumer?.level);

  const getTooltipContent = (type) => {
    switch (type) {
      case "migrated_glo":
        return {
          title: "Starten wir mit <br/> <strong>den Prämien!</strong>",
          desc: (
            <div className="glo">
              <p>Du erhältst:</p>
              <div className="bonus">
                Punkte, die von glo+ migriert wurden
                <p>+{data?.consumer?.migration?.balance}</p>
              </div>
              <div className="bonus">
                Bonus-MigrationsPunktes <p>+60</p>
              </div>
              <p>
                Dein aktueller Status
                <br /> nach der Migration:
              </p>
              <div className="onboarding-popup-level">
                <Crown />
                {level}
              </div>
            </div>
          ),
        };
      case "migrated_vuse":
        return {
          title: "Starten wir mit <br/> <strong>den Prämien!</strong>",
          desc: (
            <div className="glo">
              <p>Du erhältst:</p>
              <div className="bonus">
                Bonus-MigrationsPunktes <p>+60</p>
              </div>
              <p>
                Dein aktueller Status
                <br /> nach der Migration:
              </p>
              <div className="onboarding-popup-level">
                <Crown />
                {level}
              </div>
            </div>
          ),
        };
      case "migrated_mixed":
        return {
          title: "Starten wir mit <br/> <strong>den Prämien!</strong>",
          desc: (
            <div className="glo">
              <p>Du erhältst:</p>
              <div className="bonus">
                Punkte, die von glo+ migriert wurden
                <p>+{data?.consumer?.migration?.balance}</p>
              </div>
              <div className="bonus">
                Bonus-MigrationsPunktes <p>+60</p>
              </div>
              <p>
                Dein aktueller Status
                <br /> nach der Migration:
              </p>
              <div className="onboarding-popup-level">
                <Crown />
                {level}
              </div>
            </div>
          ),
        };
      case "new_glo":
        return {
          title: "Starten wir mit <br/> <strong>den Prämien!</strong>",
          desc: (
            <div className="newGlo">
              <p>
                Als Willkommensgeschenk erhältst <br />
                <strong>du einen Gutschein:</strong>
              </p>
              <div className="bonus">
                <p className="blue">
                  2 Packungen neo™, veo™ oder Lucky Strike for glo™-Sticks
                  gratis!
                </p>
                <p className="dark">
                  Du findest den Gutschein im <strong>Tab "Erhalten".</strong>
                </p>
              </div>
              <p>Dein aktueller Status</p>
              <div className="onboarding-popup-level">
                <Crown />
                {level}
              </div>
            </div>
          ),
        };
      case "new_vuse":
        return {
          title: "Starten wir mit <br/> <strong>den Prämien!</strong>",
          desc: (
            <div className="glo">
              <p>Du erhältst:</p>
              <div className="bonus">
                Punkte für den Beitritt <p>+30</p>
              </div>
              <p>Dein aktueller Status</p>
              <div className="onboarding-popup-level">
                <Crown />
                {level}
              </div>
            </div>
          ),
        };
      default:
        return {
          title:
            "Aktualisierung deines <strong>Profils und deiner Vorteile! </strong>",
          desc: (
            <div className="glo">
              <p>
                <strong>
                  Wir ermitteln gerade deine Punkte und deinen Status.{" "}
                </strong>
                du kannst dein Konto jetzt nutzen; wir werden deine Kategorie
                und Punkte bald aktualisieren.
              </p>
              <br />
              <p>Vielen Dank für deine Geduld!</p>
            </div>
          ),
        };
    }
  };

  const updateTooltipContent = (steps, type, data) => {
    steps.forEach((step) => {
      if (step.switchContent) {
        step.tooltipContent = getTooltipContent(type, data);
      }
    });
  };

  updateTooltipContent(stepsMap, data?.consumer?.migration?.status, data);

  function openMenu() {
    if (
      !document.querySelector("nav.navigation").classList.contains("active") &&
      document.querySelector(".navigation-controls>button.open-btn")
    ) {
      document.querySelector(".navigation-controls>button.open-btn").click();
    }
  }

  function hideMenu() {
    if (document.querySelector("nav.navigation").classList.contains("active")) {
      document.querySelector(".navigation-top-bar>button.close-btn").click();
    }
  }

  const openSubmenu = (selector) => {
    const element = document.querySelector(selector);
    if (!element.classList.contains("active")) {
      element.click();
    }
  };

  const hideSubmenu = (selector) => {
    const element = document.querySelector(selector);
    if (element.classList.contains("active")) {
      element.click();
    }
  };

  function closeOnboarding() {
    removeUrlParam("tutorial");
    document.querySelector("body").classList.remove("ReactModal__Body--open");
    navigate(location.pathname, { replace: true });
  }

  useEffect(() => {
    if (!show) {
      return;
    }

    window.scrollTo(0, 0);

    if (!stepsMap[step].menuCanHide && windowSize.width < 1020) {
      openMenu();
    }

    if (stepsMap[step].menuSelector) {
      openSubmenu(stepsMap[step].menuSelector);
    }

    setTimeout(() => {
      document.querySelector("body").classList.add("ReactModal__Body--open");

      if (!stepsMap[step].menuCanHide) {
        if (windowSize.width < 1020) {
          document.querySelector("nav.navigation").classList.add("active");
        } else {
          document
            .querySelector("nav.navigation")
            .classList.add("expanded", "active");
        }
      }

      if (stepsMap[step].menuCanHide && windowSize.width < 1020) {
        hideMenu();
      }

      if (stepsMap[step].menuCanHide && windowSize.width < 1280) {
        document.querySelector("nav.navigation").classList.remove("expanded");
      }

      element.current = document.querySelector(stepsMap[step].elementSelector);
      element.current.classList.add("active");

      if (!stepsMap[step].disableElementAutoFit) {
        element.current.style.width = "fit-content";
      }

      const { bottom, left, right, top, width, height } =
        element.current.getBoundingClientRect();

      if (windowSize.width >= 1020) {
        tooltip.current.style.left = `${
          right + stepsMap[step].tooltipPositionOffsetDesktop.right
        }px`;
        tooltip.current.style.top = `${
          top + stepsMap[step].tooltipPositionOffsetDesktop.top
        }px`;
      } else {
        tooltip.current.style.left = `${
          left + stepsMap[step].tooltipPositionOffsetMobile.left
        }px`;
        tooltip.current.style.top = `${
          bottom + stepsMap[step].tooltipPositionOffsetMobile.bottom
        }px`;
      }

      if (stepsMap[step].tooltipMobileSettings && windowSize.width < 1020) {
        Object.assign(
          tooltip.current.querySelector(".tooltip").style,
          stepsMap[step].tooltipMobileSettings({
            bottom,
            left,
            right,
            top,
            width,
            height,
          })
        );
      }

      const highlightCSSProperties = {
        height: "39px",
        width: `${width + 24}px`,
        top: `${top - (37 - height) / 2}px`,
        left: `${left - 10}px`,
        boxShadowWidth: "24px",
        borderRadius: "24px",
        ...(stepsMap[step].customHighlightStyling &&
          stepsMap[step].customHighlightStyling({
            bottom,
            left,
            right,
            top,
            width,
            height,
          })),
      };

      for (const [key, value] of Object.entries(highlightCSSProperties)) {
        highlight.current.style.setProperty(`--${key}`, value);
      }
    }, 100);

    return () => {
      if (element.current) {
        element.current.classList.remove("active");
      }

      if (step === stepsMap.length - 1) {
        document
          .querySelector("body")
          .classList.remove("ReactModal__Body--open");
        setStep(0);
      }

      if (tooltip.current) {
        tooltip.current.querySelector(".tooltip").style = "";
      }

      if (
        document.querySelector(
          "nav.navigation ul.navigation-list li.list-item a.item.active"
        )
      ) {
        hideSubmenu(stepsMap[step].menuSelector);
      }
    };
  }, [step, show]);

  if (!show) {
    return;
  }

  return (
    <div className="onboarding">
      <div
        className={`elem ${stepsMap[step]?.higlightHidden ? "hidden" : ""}`}
        ref={highlight}
      />
      <OnboardingPopup
        ref={tooltip}
        content={stepsMap[step].tooltipContent}
        currentStep={step}
        stepsLength={stepsMap.length}
        onClick={() => {
          window.scrollTo(0, 0);
          if (stepsMap[step].action) {
            stepsMap[step].action();
          }
          if (stepsMap.length <= step + 1) {
            return;
          }
          setStep((prev) => ++prev);
        }}
        onExit={closeOnboarding}
      />
    </div>
  );
}
